import { useState, useEffect } from 'react'

/**
 * Advanced Cookie Consent Banner Component
 * GDPR-compliant cookie consent with granular preferences
 */
function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false)
  const [showPreferences, setShowPreferences] = useState(false)
  const [cookiePreferences, setCookiePreferences] = useState({
    essential: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false,
    functional: false
  })

  // Initialize cookies based on preferences
  const initializeCookies = (preferences) => {
    // Essential cookies are always enabled
    if (preferences.essential) {
      // Essential cookies initialization (if needed)
    }

    // Analytics cookies
    if (preferences.analytics) {
      // Initialize Google Analytics, etc.
      // Example: gtag('consent', 'update', { analytics_storage: 'granted' })
    } else {
      // Disable analytics
      // Example: gtag('consent', 'update', { analytics_storage: 'denied' })
    }

    // Marketing cookies
    if (preferences.marketing) {
      // Initialize marketing pixels, etc.
    } else {
      // Disable marketing
    }

    // Functional cookies
    if (preferences.functional) {
      // Initialize functional features
    } else {
      // Disable functional
    }
  }

  // Check if user has already made a choice
  useEffect(() => {
    try {
      const consent = localStorage.getItem('cookieConsent')
      if (!consent) {
        // Show banner after a short delay for better UX
        setTimeout(() => setShowBanner(true), 1000)
      } else {
        // Load saved preferences
        const savedPreferences = JSON.parse(consent)
        setCookiePreferences(savedPreferences)
      }
    } catch (error) {
      // If localStorage is not available, show banner anyway
      console.warn('localStorage not available:', error)
      setTimeout(() => setShowBanner(true), 1000)
    }
  }, [])

  // Handle accept all
  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      marketing: true,
      functional: true
    }
    setCookiePreferences(allAccepted)
    try {
      localStorage.setItem('cookieConsent', JSON.stringify(allAccepted))
      localStorage.setItem('cookieConsentDate', new Date().toISOString())
    } catch (error) {
      console.warn('Failed to save cookie consent:', error)
    }
    setShowBanner(false)
    setShowPreferences(false)
    
    // Trigger analytics/marketing scripts if needed
    initializeCookies(allAccepted)
  }

  // Handle reject all (only essential)
  const handleRejectAll = () => {
    const onlyEssential = {
      essential: true,
      analytics: false,
      marketing: false,
      functional: false
    }
    setCookiePreferences(onlyEssential)
    try {
      localStorage.setItem('cookieConsent', JSON.stringify(onlyEssential))
      localStorage.setItem('cookieConsentDate', new Date().toISOString())
    } catch (error) {
      console.warn('Failed to save cookie consent:', error)
    }
    setShowBanner(false)
    setShowPreferences(false)
    
    // Remove non-essential cookies
    initializeCookies(onlyEssential)
  }

  // Handle save preferences
  const handleSavePreferences = () => {
    try {
      localStorage.setItem('cookieConsent', JSON.stringify(cookiePreferences))
      localStorage.setItem('cookieConsentDate', new Date().toISOString())
    } catch (error) {
      console.warn('Failed to save cookie consent:', error)
    }
    setShowBanner(false)
    setShowPreferences(false)
    
    // Initialize cookies based on preferences
    initializeCookies(cookiePreferences)
  }

  // Toggle individual cookie category
  const toggleCookieCategory = (category) => {
    if (category === 'essential') return // Essential cookies cannot be disabled
    setCookiePreferences(prev => ({
      ...prev,
      [category]: !prev[category]
    }))
  }

  // Don't render if banner shouldn't be shown
  if (!showBanner && !showPreferences) return null

  return (
    <>
      {/* Cookie Consent Banner */}
      {showBanner && !showPreferences && (
        <div className="fixed bottom-0 left-0 right-0 z-50 animate-slide-up">
          <div className="bg-white border-t-2 border-purple-600 shadow-2xl">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    🍪 We Value Your Privacy
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. 
                    By clicking "Accept All", you consent to our use of cookies. You can also customize your preferences 
                    or reject non-essential cookies.
                  </p>
                  <a 
                    href="/privacy-policy" 
                    className="text-sm text-purple-600 hover:text-purple-700 underline mt-2 inline-block"
                  >
                    Learn more in our Privacy Policy
                  </a>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                  <button
                    onClick={() => setShowPreferences(true)}
                    className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 whitespace-nowrap"
                  >
                    Customize
                  </button>
                  <button
                    onClick={handleRejectAll}
                    className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200 whitespace-nowrap"
                  >
                    Reject All
                  </button>
                  <button
                    onClick={handleAcceptAll}
                    className="px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-purple-700 rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all duration-200 shadow-md hover:shadow-lg whitespace-nowrap"
                  >
                    Accept All
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cookie Preferences Modal */}
      {showPreferences && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-scale-in">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">Cookie Preferences</h2>
                <button
                  onClick={() => setShowPreferences(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                  aria-label="Close preferences"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="px-6 py-6 space-y-6">
              <p className="text-gray-600 text-sm leading-relaxed">
                Manage your cookie preferences. You can enable or disable different types of cookies below. 
                Essential cookies are required for the website to function and cannot be disabled.
              </p>

              {/* Essential Cookies */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">Essential Cookies</h3>
                      <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-700 rounded">
                        Always Active
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">
                      These cookies are necessary for the website to function and cannot be switched off. 
                      They are usually set in response to actions made by you, such as setting privacy preferences, 
                      logging in, or filling in forms.
                    </p>
                    <p className="text-xs text-gray-500">
                      Examples: Session management, security, load balancing
                    </p>
                  </div>
                  <div className="ml-4">
                    <div className="relative inline-block w-14 h-8 bg-purple-600 rounded-full opacity-50 cursor-not-allowed">
                      <div className="absolute right-1 top-1 w-6 h-6 bg-white rounded-full"></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Analytics Cookies</h3>
                    <p className="text-sm text-gray-600 mb-2">
                      These cookies help us understand how visitors interact with our website by collecting 
                      and reporting information anonymously. This helps us improve our website's performance.
                    </p>
                    <p className="text-xs text-gray-500">
                      Examples: Google Analytics, page views, user behavior tracking
                    </p>
                  </div>
                  <div className="ml-4">
                    <button
                      onClick={() => toggleCookieCategory('analytics')}
                      className={`relative inline-block w-14 h-8 rounded-full transition-colors duration-200 ${
                        cookiePreferences.analytics ? 'bg-purple-600' : 'bg-gray-300'
                      }`}
                      aria-label="Toggle analytics cookies"
                    >
                      <div
                        className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-transform duration-200 ${
                          cookiePreferences.analytics ? 'translate-x-6 right-1' : 'translate-x-0 left-1'
                        }`}
                      ></div>
                    </button>
                  </div>
                </div>
              </div>

              {/* Marketing Cookies */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Marketing Cookies</h3>
                    <p className="text-sm text-gray-600 mb-2">
                      These cookies are used to deliver advertisements that are more relevant to you and your interests. 
                      They may also be used to limit the number of times you see an advertisement.
                    </p>
                    <p className="text-xs text-gray-500">
                      Examples: Ad targeting, remarketing pixels, social media tracking
                    </p>
                  </div>
                  <div className="ml-4">
                    <button
                      onClick={() => toggleCookieCategory('marketing')}
                      className={`relative inline-block w-14 h-8 rounded-full transition-colors duration-200 ${
                        cookiePreferences.marketing ? 'bg-purple-600' : 'bg-gray-300'
                      }`}
                      aria-label="Toggle marketing cookies"
                    >
                      <div
                        className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-transform duration-200 ${
                          cookiePreferences.marketing ? 'translate-x-6 right-1' : 'translate-x-0 left-1'
                        }`}
                      ></div>
                    </button>
                  </div>
                </div>
              </div>

              {/* Functional Cookies */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Functional Cookies</h3>
                    <p className="text-sm text-gray-600 mb-2">
                      These cookies enable enhanced functionality and personalization, such as remembering your preferences 
                      and settings. They may be set by us or by third-party providers.
                    </p>
                    <p className="text-xs text-gray-500">
                      Examples: Language preferences, region settings, video player preferences
                    </p>
                  </div>
                  <div className="ml-4">
                    <button
                      onClick={() => toggleCookieCategory('functional')}
                      className={`relative inline-block w-14 h-8 rounded-full transition-colors duration-200 ${
                        cookiePreferences.functional ? 'bg-purple-600' : 'bg-gray-300'
                      }`}
                      aria-label="Toggle functional cookies"
                    >
                      <div
                        className={`absolute top-1 w-6 h-6 bg-white rounded-full transition-transform duration-200 ${
                          cookiePreferences.functional ? 'translate-x-6 right-1' : 'translate-x-0 left-1'
                        }`}
                      ></div>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 rounded-b-2xl">
              <div className="flex flex-col sm:flex-row gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowPreferences(false)
                    setShowBanner(true)
                  }}
                  className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePreferences}
                  className="px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-purple-700 rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all duration-200 shadow-md hover:shadow-lg"
                >
                  Save Preferences
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default CookieConsent

